import clsx from 'clsx'
import React, { useEffect } from 'react'
import { Icon, Text } from 'ui'
import { NAVIGATION_ITEMS } from '~/constants/common'
import { useNavigationContext } from '~/context/NavigationContext'
import classes from '~/styles/components/AppNavigation.module.scss'

import { Anchor, Avatar, Button, CloseButton, Drawer, Flex } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { Link, NavLink, useLocation, useNavigation } from '@remix-run/react'

export interface AppNavigationArgs {
  firstName: string
  lastName: string
}

export const AppNavigation: React.FC<AppNavigationArgs> = ({ firstName, lastName }) => {
  const desktop = useMediaQuery('(min-width: 450px)')
  const { isUSUser } = useNavigationContext()

  const shouldAddLeftSpaceToBody = useMediaQuery('(min-width: 450px) and (max-width: 768px)')
  const navigation = useNavigation()

  const [opened, { open, close, toggle }] = useDisclosure(false)
  const location = useLocation()

  const filteredNavigationItems = isUSUser ? NAVIGATION_ITEMS.slice(0, -2) : NAVIGATION_ITEMS
  useEffect(() => {
    if (navigation.state === 'loading') close()
  }, [navigation.state])

  useEffect(() => {
    if (shouldAddLeftSpaceToBody) document.body.classList.add('left-nav-panel-space')
    else document.body.classList.remove('left-nav-panel-space')

    return () => {
      document.body.classList.remove('left-nav-panel-space')
    }
  }, [shouldAddLeftSpaceToBody])

  const isInternalLink = (href: string) => href?.startsWith('/')
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const [path, hash] = href?.split('#')
    if (path === location?.pathname) {
      e?.preventDefault()
      const target = hash && document.getElementById(hash)
      target
        ? target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        : window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <>
      <nav className={classes.sidebar}>
        <Icon name="desktopnavBg" className={classes.bg} />
        <ul>
          {filteredNavigationItems.map((item) => {
            const internal = isInternalLink(item.href)

            const content = (
              <>
                <div className={classes.hoverBox}>
                  <div className={classes.pointer}></div>
                  <div className={classes.circle}></div>
                  <img src={item.hoverSrc} alt={item.label} />
                  <span className={classes.cicleLabel}>{item.label}</span>
                </div>
                <div className={classes.iconWrapper}>
                  <img src={item.src} alt={item.label} />
                </div>
              </>
            )

            return (
              <li key={item.label} className={classes.navItem}>
                {internal ? (
                  <Link to={item.href} onClick={(e) => handleClick(e, item.href)}>
                    {content}
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.link}
                  >
                    {content}
                  </a>
                )}
              </li>
            )
          })}
        </ul>
      </nav>

      <div className={classes.desktopWrapper}>
        <Flex>
          <Button onClick={toggle} className={classes.mobileMenuButton}>
            <Icon name="menuLinesMobile" />
          </Button>
          <Drawer
            opened={opened && !desktop}
            onClose={close}
            className={classes.mobileDrawer}
            autoFocus
          >
            <Flex className={classes.mobileContainer}>
              <Icon
                name="mobileNavigationWave"
                ariaHidden
                className={classes.mobileNavigationWave}
              />
              <CloseButton
                className={classes.mobileCloseMenuButton}
                aria-label="Close navigation"
                onClick={close}
              />
              <Flex className={classes.mobileUserDetailsContainer}>
                <NavLink className={classes.mobileLink} to="/dashboard/myProfile">
                  <Avatar className={classes.mobileUserAvatar} alt={`${firstName} ${lastName}`}>
                    {firstName?.[0]}
                    {lastName?.[0]}
                  </Avatar>
                </NavLink>
                <NavLink className={classes.mobileLink} to="/dashboard/myProfile">
                  <span className={classes.mobileUserFullName} aria-label="User's full name">
                    {firstName} {lastName}
                  </span>
                </NavLink>
              </Flex>
              <nav className={classes.mobileNav}>
                <Flex role="list" className={classes.mobileList}>
                  <Flex role="listitem" className={classes.mobileListItem}>
                    <NavLink
                      className={classes.mobileLink}
                      to="/dashboard"
                      aria-label="Account Summary"
                    >
                      <Icon name="summary" />
                      <Text component="span" className={classes.mobileLinkText}>
                        Account Summary
                      </Text>
                    </NavLink>
                  </Flex>
                  <Flex role="listitem" className={classes.mobileListItem}>
                    <NavLink
                      className={classes.mobileLink}
                      to="/inactive-applications"
                      aria-label="Inactive Applications"
                    >
                      <Icon name="inactiveApplicationsMobile" />
                      <Text component="span" className={classes.mobileLinkText}>
                        Inactive Applications
                      </Text>
                    </NavLink>
                  </Flex>
                  <Flex role="listitem" className={classes.mobileListItem}>
                    <NavLink
                      className={classes.mobileLink}
                      to="/dashboard/bank-account"
                      aria-label="Bank Accounts"
                    >
                      <Icon name="bankAccountMobile" />
                      <Text component="span" className={classes.mobileLinkText}>
                        Bank Accounts
                      </Text>
                    </NavLink>
                  </Flex>
                  <Flex role="listitem" className={classes.mobileListItem}>
                    <Anchor
                      className={classes.mobileLink}
                      href="https://support.mpowerfinancing.com/hc/en-us"
                      target="_blank"
                      aria-label="Support Center"
                    >
                      <Icon name="supportMobile" />
                      <Text component="span" className={classes.mobileLinkText}>
                        Support Center
                      </Text>
                    </Anchor>
                  </Flex>

                  <Flex role="listitem" className={classes.mobileListItem}>
                    <Anchor
                      className={classes.mobileLink}
                      href="https://www.mpowerfinancing.com/get-a-loan/refer"
                      target="_blank"
                      aria-label="Refer a Friend"
                    >
                      <Icon name="referMobile" />
                      <Text component="span" className={classes.mobileLinkText}>
                        Refer a Friend
                      </Text>
                    </Anchor>
                  </Flex>

                  <Flex role="listitem" className={classes.mobileListItem}>
                    <Anchor
                      className={classes.mobileLink}
                      href="https://www.mpowerfinancing.com/blog/mpowers-top-faqs"
                      target="_blank"
                      aria-label="FAQ"
                    >
                      <Icon name="faqMobile" />
                      <Text component="span" className={classes.mobileLinkText}>
                        FAQ
                      </Text>
                    </Anchor>
                  </Flex>

                  <Flex role="listitem" className={classes.mobileListItem}>
                    <NavLink
                      className={classes.mobileLink}
                      to="/dashboard/myProfile"
                      aria-label="Settings"
                    >
                      <Icon name="settings" />
                      <Text component="span" className={classes.mobileLinkText}>
                        Settings
                      </Text>
                    </NavLink>
                  </Flex>

                  {/*NOTE: We hide this for now - https://mpowerfinancing.atlassian.net/browse/MPW-13708*/}
                  {/*<Flex role="listitem" className={classes.mobileListItem}>*/}
                  {/*  <NavLink className={classes.mobileLink} to="/" aria-label="Rate Us">*/}
                  {/*    <FeedbackMobileIcon />*/}
                  {/*    <Text component="span" className={classes.mobileLinkText}>*/}
                  {/*      Rate Us*/}
                  {/*    </Text>*/}
                  {/*  </NavLink>*/}
                  {/*</Flex>*/}
                  <Flex role="listitem" className={classes.mobileListItem}>
                    <Anchor
                      className={classes.mobileLink}
                      href="https://www.mpowerfinancing.com/terms-of-service"
                      target="_blank"
                      aria-label=" T & C"
                    >
                      <Icon name="termsMobile" />
                      <Text component="span" className={classes.mobileLinkText}>
                        T & C
                      </Text>
                    </Anchor>
                  </Flex>
                  <Flex role="listitem" className={classes.mobileListItem}>
                    <NavLink className={classes.mobileLink} to="/logout" aria-label="Log out">
                      <Icon name="logout" />
                      <Text component="span" className={classes.mobileLinkText}>
                        Log Out
                      </Text>
                    </NavLink>
                  </Flex>
                </Flex>
              </nav>
            </Flex>
          </Drawer>
        </Flex>
      </div>
    </>
  )
}
